// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xGoogleAnalyticsUpdate } from "@guestsupply/App/Handlers/GoogleAnalytics/xGoogleAnalytics/xGoogleAnalytics";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import { parseProductCategories } from "@guestsupply/XDL/Utilities";
import color from "@guestsupply/XDL/Utilities/color";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getSelectedProductPath, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getComputedVariantProduct,
    getProductsDataView,
    getProductState,
    getProductStateByPath,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type ProductData = Pick<ProductModel, "productNumber" | "productTitle" | "brand" | "properties"> & {
    pricing: ProductPriceDto | undefined;
};

const mapStateToProps = (state: CustomAppState) => {
    let PDPProduct: ProductData | null = null;

    const currentPage = getCurrentPage(state).type;
    const location = getLocation(state);
    const isPDP = currentPage === "ProductDetailsPage";
    const isCartPage = currentPage === "CartPage";
    const isListDetailsPage = currentPage === "MyListsDetailsPage";
    const currentBillTo = getCurrentBillToState(state).value;
    const session = getSession(state);

    if (isPDP) {
        const productPath =
            getSelectedProductPath(state) ||
            (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
        const productState = getProductStateByPath(state, productPath);
        const variantProductState = getProductState(state, state.pages.productDetails.selectedProductId);
        const computedVariantProduct = getComputedVariantProduct(productState, variantProductState);
        const productInfo =
            computedVariantProduct.value && state.pages.productDetails.productInfosById
                ? state.pages.productDetails.productInfosById[computedVariantProduct.value.id]
                : undefined;

        if (productState?.value) {
            const { productNumber, productTitle, brand, properties } = productState.value;
            PDPProduct = {
                productNumber,
                productTitle,
                brand,
                properties,
                pricing: productInfo?.pricing as ProductPriceDto,
            };
        }
    }

    return {
        currentPage,
        PDPProduct,
        cart: getCurrentCartState(state),
        xGoogleAnalytics: state.xGoogleAnalytics,
        currencyCode: state.context?.session?.currency?.currencyCode,
        isPDP,
        isCartPage,
        billToProperties: currentBillTo?.properties,
        companyName: currentBillTo?.companyName,
        customerNumber: currentBillTo?.customerNumber,
        locationId: session?.pickUpWarehouse?.id,
        isListDetailsPage,
        wishList: getWishListState(state, state.pages.myListDetails.wishListId).value,
        userEmail: session.email,
    };
};

const mapDispatchToProps = {
    xGoogleAnalyticsUpdate,
};

const enum fields {
    includeAddToCartEvent = "includeAddToCartEvent",
    includeUpdateCartEvent = "includeUpdateCartEvent",
    includePurchaseEvent = "includePurchaseEvent",
    includeRemoveFromCartLineEvent = "includeRemoveFromCartLineEvent",
    includeSelectPromotionEvent = "includeSelectPromotionEvent",
    includeViewCartEvent = "includeViewCartEvent",
    includeMyAccountQuickLinks = "includeMyAccountQuickLinks",
    includeMyAccountRecentOrders = "includeMyAccountRecentOrders",
    includeMyAccountMyLists = "includeMyAccountMyLists",
    includeMyAccountRequisitions = "includeMyAccountRequisitions",
    includeMyAccountOrderApproval = "includeMyAccountOrderApproval",
    includeMyAccountInvoiceHistory = "includeMyAccountInvoiceHistory",
    includePLPSearchEvent = "includePLPSearchEvent",
    includePLPFilterEvent = "includePLPFilterEvent",
    includePaymentPortalEvent = "includePaymentPortalEvent",
    includeShareProductEvent = "includeShareProductEvent",
    includeRMARequestEvent = "includeRMARequestEvent",
    includeBMCancelProducts = "includeBMCancelProducts",
    includeBMAlternateDC = "includeBMAlternateDC",
    includeReorderEvent = "includeReorderEvent",
    includeOrderHistoryEvent = "includeOrderHistoryEvent",
    includeAutoDeliveryEvent = "includeAutoDeliveryEvent",
    includeScheduleReminderEvent = "includeScheduleReminderEvent",
    includeLoginEvent = "includeLoginEvent",
    includeRequestAccessEvent = "includeRequestAccessEvent",
    includeNavigationBarEvent = "includeNavigationBarEvent",
    includeToolBarNavigationEvent = "includeToolBarNavigationEvent",
    includeMyAccountShortcutEvent = "includeMyAccountShortcutEvent",
    includePopularCategoriesEvent = "includePopularCategoriesEvent",
    includeFeaturedItemsEvent = "includeFeaturedItemsEvent",
    includeFeaturedPartnersEvent = "includeFeaturedPartnersEvent",
    includeOurSolutionsEvent = "includeOurSolutionsEvent",
    includeSolutionsDeskBlogEvent = "includeSolutionsDeskBlogEvent",
    includeMyAccountRecentOrdersHomePage = "includeMyAccountRecentOrdersHomePage",
    includeHomePageSlider = "includeHomePageSlider",
    includeMyAccountFlyout = "includeMyAccountFlyout",
    includeMyOrderGuideCategory = "includeMyOrderGuideCategory",
    includeMyOrderGuideSearch = "includeMyOrderGuideSearch",
    includeSearchResult = "includeSearchResult",
    includeViewItemListEvent = "includeViewItemListEvent",
    includeSelectItemListEvent = "includeSelectItemListEvent",
    includeAddToWishlistEvent = "includeAddToWishlistEvent",
    includeAddQuickOrderEvent = "includeAddQuickOrderEvent",
    includeBeginCheckoutEvent = "includeBeginCheckoutEvent",
    includeCheckoutShippingInfoEvent = "includeCheckoutShippingInfoEvent",
    includeCheckoutPaymentEvent = "includeCheckoutPaymentEvent",
    includeViewPromotionEvent = "includeViewPromotionEvent",
    includeRegistrationEvent = "includeRegistrationEvent",
    includePDPViewEvent = "includePDPViewEvent",
    includeCustomVirtualPageViewEvent = "includeCustomVirtualPageViewEvent",
    includePunchOutCheckout = "includePunchOutCheckout",
    includeCancelPunchOutCheckout = "includeCancelPunchOutCheckout",
    includeNewAutoDeliveryEvent = "includeNewAutoDeliveryEvent",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.includeAddToCartEvent]: boolean;
        [fields.includeUpdateCartEvent]: boolean;
        [fields.includePurchaseEvent]: boolean;
        [fields.includeRemoveFromCartLineEvent]: boolean;
        [fields.includeSelectPromotionEvent]: boolean;
        [fields.includeViewCartEvent]: boolean;
        [fields.includeMyAccountQuickLinks]: boolean;
        [fields.includeMyAccountRecentOrders]: boolean;
        [fields.includeMyAccountMyLists]: boolean;
        [fields.includeMyAccountRequisitions]: boolean;
        [fields.includeMyAccountOrderApproval]: boolean;
        [fields.includeMyAccountInvoiceHistory]: boolean;
        [fields.includePLPSearchEvent]: boolean;
        [fields.includePLPFilterEvent]: boolean;
        [fields.includePaymentPortalEvent]: boolean;
        [fields.includeShareProductEvent]: boolean;
        [fields.includeRMARequestEvent]: boolean;
        [fields.includeBMCancelProducts]: boolean;
        [fields.includeBMAlternateDC]: boolean;
        [fields.includeReorderEvent]: boolean;
        [fields.includeOrderHistoryEvent]: boolean;
        [fields.includeAutoDeliveryEvent]: boolean;
        [fields.includeScheduleReminderEvent]: boolean;
        [fields.includeLoginEvent]: boolean;
        [fields.includeRequestAccessEvent]: boolean;
        [fields.includeNavigationBarEvent]: boolean;
        [fields.includeToolBarNavigationEvent]: boolean;
        [fields.includeMyAccountShortcutEvent]: boolean;
        [fields.includePopularCategoriesEvent]: boolean;
        [fields.includeFeaturedItemsEvent]: boolean;
        [fields.includeFeaturedPartnersEvent]: boolean;
        [fields.includeOurSolutionsEvent]: boolean;
        [fields.includeSolutionsDeskBlogEvent]: boolean;
        [fields.includeMyAccountRecentOrdersHomePage]: boolean;
        [fields.includeHomePageSlider]: boolean;
        [fields.includeMyAccountFlyout]: boolean;
        [fields.includeMyOrderGuideCategory]: boolean;
        [fields.includeMyOrderGuideSearch]: boolean;
        [fields.includeSearchResult]: boolean;
        [fields.includeViewItemListEvent]: boolean;
        [fields.includeSelectItemListEvent]: boolean;
        [fields.includeAddToWishlistEvent]: boolean;
        [fields.includeAddQuickOrderEvent]: boolean;
        [fields.includeBeginCheckoutEvent]: boolean;
        [fields.includeCheckoutShippingInfoEvent]: boolean;
        [fields.includeCheckoutPaymentEvent]: boolean;
        [fields.includeViewPromotionEvent]: boolean;
        [fields.includeRegistrationEvent]: boolean;
        [fields.includePDPViewEvent]: boolean;
        [fields.includeCustomVirtualPageViewEvent]: boolean;
        [fields.includePunchOutCheckout]: boolean;
        [fields.includeCancelPunchOutCheckout]: boolean;
        [fields.includeNewAutoDeliveryEvent]: boolean;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface GoogleAnalyticsWidgetStyles {
    wrapper?: InjectableCss;
    instructionText?: TypographyPresentationProps;
}

export const googleAnalyticsWidgetStyles: GoogleAnalyticsWidgetStyles = {
    wrapper: {
        css: css`
            padding: 10px;
            background-color: ${color("secondary.main")};
        `,
    },
    instructionText: {
        color: "primary.contrast",
        variant: "h6",
        css: css`
            margin: 0;
        `,
    },
};

const GoogleAnalyticsWidget: FC<Props> = ({
    xGoogleAnalyticsUpdate,
    fields,
    currentPage,
    PDPProduct,
    cart,
    xGoogleAnalytics,
    currencyCode,
    shellContext,
    isPDP,
    isCartPage,
    billToProperties,
    companyName,
    customerNumber,
    locationId,
    isListDetailsPage,
    wishList,
    userEmail,
}) => {
    const styles = googleAnalyticsWidgetStyles;
    if (shellContext.isInShell && shellContext.isEditing) {
        // Placeholder in CMS view
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.instructionText}>{translate("Google Analytics Widget (GA4)")}</Typography>
            </StyledWrapper>
        );
    }
    const [PDPInfoLoaded, setPDPInfoLoaded] = useState<boolean>(false);

    useEffect(() => {
        xGoogleAnalyticsUpdate({
            includeAddToCartEvent: fields["includeAddToCartEvent"] || false,
            includeUpdateCartEvent: fields["includeUpdateCartEvent"] || false,
            includePurchaseEvent: fields["includePurchaseEvent"] || false,
            includeRemoveFromCartLineEvent: fields["includeRemoveFromCartLineEvent"] || false,
            includeSelectPromotionEvent: fields["includeSelectPromotionEvent"] || false,
            includeViewCartEvent: fields["includeViewCartEvent"] || false,
            includeMyAccountQuickLinks: fields["includeMyAccountQuickLinks"] || false,
            includeMyAccountRecentOrders: fields["includeMyAccountRecentOrders"] || false,
            includeMyAccountMyLists: fields["includeMyAccountMyLists"] || false,
            includeMyAccountRequisitions: fields["includeMyAccountRequisitions"] || false,
            includeMyAccountOrderApproval: fields["includeMyAccountOrderApproval"] || false,
            includeMyAccountInvoiceHistory: fields["includeMyAccountInvoiceHistory"] || false,
            includePLPSearchEvent: fields["includePLPSearchEvent"] || false,
            includePLPFilterEvent: fields["includePLPFilterEvent"] || false,
            includePaymentPortalEvent: fields["includePaymentPortalEvent"] || false,
            includeShareProductEvent: fields["includeShareProductEvent"] || false,
            includeRMARequestEvent: fields["includeRMARequestEvent"] || false,
            includeBMCancelProducts: fields["includeBMCancelProducts"] || false,
            includeBMAlternateDC: fields["includeBMAlternateDC"] || false,
            includeReorderEvent: fields["includeReorderEvent"] || false,
            includeOrderHistoryEvent: fields["includeOrderHistoryEvent"] || false,
            includeAutoDeliveryEvent: fields["includeAutoDeliveryEvent"] || false,
            includeScheduleReminderEvent: fields["includeScheduleReminderEvent"] || false,
            includeLoginEvent: fields["includeLoginEvent"] || false,
            includeRequestAccessEvent: fields["includeRequestAccessEvent"] || false,
            includeNavigationBarEvent: fields["includeNavigationBarEvent"] || false,
            includeToolBarNavigationEvent: fields["includeToolBarNavigationEvent"] || false,
            includeMyAccountShortcutEvent: fields["includeMyAccountShortcutEvent"] || false,
            includePopularCategoriesEvent: fields["includePopularCategoriesEvent"] || false,
            includeFeaturedItemsEvent: fields["includeFeaturedItemsEvent"] || false,
            includeFeaturedPartnersEvent: fields["includeFeaturedPartnersEvent"] || false,
            includeOurSolutionsEvent: fields["includeOurSolutionsEvent"] || false,
            includeSolutionsDeskBlogEvent: fields["includeSolutionsDeskBlogEvent"] || false,
            includeMyAccountRecentOrdersHomePage: fields["includeMyAccountRecentOrdersHomePage"] || false,
            includeHomePageSlider: fields["includeHomePageSlider"] || false,
            includeMyAccountFlyout: fields["includeMyAccountFlyout"] || false,
            includeMyOrderGuideCategory: fields["includeMyOrderGuideCategory"] || false,
            includeMyOrderGuideSearch: fields["includeMyOrderGuideSearch"] || false,
            includeSearchResult: fields["includeSearchResult"] || false,
            includeViewItemListEvent: fields["includeViewItemListEvent"] || false,
            includeSelectItemListEvent: fields["includeSelectItemListEvent"] || false,
            includeAddToWishlistEvent: fields["includeAddToWishlistEvent"] || false,
            includeAddQuickOrderEvent: fields["includeAddQuickOrderEvent"] || false,
            includeBeginCheckoutEvent: fields["includeBeginCheckoutEvent"] || false,
            includeCheckoutShippingInfoEvent: fields["includeCheckoutShippingInfoEvent"] || false,
            includeCheckoutPaymentEvent: fields["includeCheckoutPaymentEvent"] || false,
            includeViewPromotionEvent: fields["includeViewPromotionEvent"] || false,
            includeRegistrationEvent: fields["includeRegistrationEvent"] || false,
            includePDPViewEvent: fields["includePDPViewEvent"] || false,
            includeCustomVirtualPageViewEvent: fields["includeCustomVirtualPageViewEvent"] || false,
            includePunchOutCheckout: fields["includePunchOutCheckout"] || false,
            includeCancelPunchOutCheckout: fields["includeCancelPunchOutCheckout"] || false,
            includeNewAutoDeliveryEvent: fields["includeNewAutoDeliveryEvent"] || false,
        });
    }, [fields]);

    useEffect(() => {
        const dataLayer = getDataLayer();
        if (dataLayer) {
            if (isPDP && xGoogleAnalytics.includePDPViewEvent && PDPProduct && PDPProduct?.pricing && !PDPInfoLoaded) {
                setPDPInfoLoaded(true);
                const googleAnalyticsData = {
                    event: GOOGLE_EVENTS.VIEW_ITEM,
                    ecommerce: {
                        currency: currencyCode,
                        value: PDPProduct.pricing?.unitNetPrice,
                        items: [
                            {
                                item_id: PDPProduct?.productNumber,
                                item_name: PDPProduct?.productTitle,
                                affiliation: companyName,
                                index: 0,
                                item_brand: PDPProduct.brand?.name,
                                location_id: locationId,
                                price: PDPProduct.pricing?.unitNetPrice,
                                quantity: 1,
                                ...parseProductCategories(PDPProduct?.properties?.categories),
                            },
                        ],
                        brand_code: billToProperties?.brand,
                        exclusive: billToProperties?.exclusive,
                        group_descr: billToProperties?.groupDescription,
                        customer_number: customerNumber,
                    },
                };

                dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
                dataLayer.push(googleAnalyticsData);
            }
        }
    }, [PDPProduct, currentPage, xGoogleAnalytics]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (!cart || !cart.value || cart.isLoading) {
            return;
        }

        if (isCartPage && xGoogleAnalytics.includeViewCartEvent) {
            setPDPInfoLoaded(false);

            dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.

            const googleAnalyticsData = {
                event: GOOGLE_EVENTS.VIEW_CART,
                ecommerce: {
                    currency: currencyCode,
                    value: cart.value.orderSubTotal,
                    items: cart.value?.cartLines?.map((cartItem, idx) => ({
                        item_id: cartItem.erpNumber,
                        item_name: cartItem.shortDescription,
                        affiliation: companyName,
                        index: idx,
                        item_brand: cartItem.brand?.name,
                        location_id: locationId,
                        price: cartItem.pricing?.unitNetPrice,
                        quantity: cartItem.qtyOrdered,
                        ...parseProductCategories(cartItem?.properties?.categories),
                    })),
                    brand_code: billToProperties?.brand,
                    exclusive: billToProperties?.exclusive,
                    group_descr: billToProperties?.groupDescription,
                    customer_number: customerNumber,
                },
            };

            dataLayer.push(googleAnalyticsData);
        }
    }, [currentPage, cart]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (isListDetailsPage && xGoogleAnalytics.includeViewItemListEvent) {
            const googleAnalyticsData = {
                event: GOOGLE_EVENTS.VIEW_ITEM_LIST,
                ecommerce: {
                    item_list_id: wishList?.id,
                    item_list_name: wishList?.name,
                    items: wishList?.wishListLineCollection?.map((wishlistLineItem, idx) => ({
                        item_id: wishlistLineItem?.erpNumber,
                        item_name: wishlistLineItem?.shortDescription,
                        affiliation: companyName,
                        index: idx,
                        item_brand: wishlistLineItem?.brand?.name,
                        item_list_id: wishList?.id,
                        item_list_name: wishList?.name,
                        location_id: locationId,
                        price: wishlistLineItem.pricing?.unitNetPrice,
                        quantity: wishlistLineItem.qtyOrdered,
                        ...parseProductCategories(wishlistLineItem?.properties?.categories, false),
                    })),
                    brand_code: billToProperties?.brand,
                    exclusive: billToProperties?.exclusive,
                    group_descr: billToProperties?.groupDescription,
                },
            };

            dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            dataLayer.push(googleAnalyticsData);
        }
    }, [currentPage, isListDetailsPage, wishList]);

    useEffect(() => {
        if (userEmail && xGoogleAnalytics.includeCustomVirtualPageViewEvent) {
            const dataLayer = getDataLayer();
            const linkTitle = window.document.title;
            const linkUrl = window.location.pathname + window.location.search;

            const googleAnalyticsData = {
                event: GOOGLE_EVENTS.CUSTOM_VIRTUAL_PAGE_VIEW,
                page: {
                    title: linkTitle,
                    url: linkUrl,
                },
                email: userEmail,
            };

            dataLayer.push(googleAnalyticsData);
        }
    }, [currentPage, userEmail]);

    return null;
};

const eventsTab = {
    displayName: "Events",
    sortOrder: 1,
};
const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(GoogleAnalyticsWidget)),
    definition: {
        displayName: "Google Analytics Management (GA4)",
        group: "Xngage" as any,
        fieldDefinitions: [
            {
                name: fields.includeAddToCartEvent,
                displayName: "Include Add To Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.includeUpdateCartEvent,
                displayName: "Include Update Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.includeRemoveFromCartLineEvent,
                displayName: "Include Remove Cart Line (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.includeViewCartEvent,
                displayName: "Include View Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.includePurchaseEvent,
                displayName: "Include Purchase (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.includeSelectPromotionEvent,
                displayName: "Include Select Promotion Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.includeMyAccountQuickLinks,
                displayName: "Include My Account Quick Links (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeMyAccountRecentOrders,
                displayName: "Include My Account Recent Orders (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeMyAccountMyLists,
                displayName: "Include My Account My Lists (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeMyAccountRequisitions,
                displayName: "Include My Account Requisitions (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeMyAccountOrderApproval,
                displayName: "Include My Account Order Approval (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeMyAccountInvoiceHistory,
                displayName: "Include My Account Invoice History (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includePLPSearchEvent,
                displayName: "Include PLP Search Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includePLPFilterEvent,
                displayName: "Include PLP Filter Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includePaymentPortalEvent,
                displayName: "Include Payment Portal (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeShareProductEvent,
                displayName: "Include Share Product (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeRMARequestEvent,
                displayName: "Include RMA Request (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeBMCancelProducts,
                displayName: "Include BM Cancel Products (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeBMAlternateDC,
                displayName: "Include BM Alternate DC (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeReorderEvent,
                displayName: "Include Reorder (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeOrderHistoryEvent,
                displayName: "Include Order History (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeAutoDeliveryEvent,
                displayName: "Include Auto Delivery (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeScheduleReminderEvent,
                displayName: "Include Schedule Reminder (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeLoginEvent,
                displayName: "Include Login Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeRequestAccessEvent,
                displayName: "Include Request Access Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeNavigationBarEvent,
                displayName: "Include Navigation Bar Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeToolBarNavigationEvent,
                displayName: "Include Tool Bar Navigation Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeMyAccountShortcutEvent,
                displayName: "Include My Account Shortcut Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includePopularCategoriesEvent,
                displayName: "Include Popular Categories Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeFeaturedItemsEvent,
                displayName: "Include Featured Items Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeFeaturedPartnersEvent,
                displayName: "Include Featured Partners Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeOurSolutionsEvent,
                displayName: "Include Our Solutions Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeSolutionsDeskBlogEvent,
                displayName: "Include Solutions Desk Blog Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeMyAccountRecentOrdersHomePage,
                displayName: "Include My Account Recent Orders Home Page Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeHomePageSlider,
                displayName: "Include Home Page Slider Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeMyAccountFlyout,
                displayName: "Include My Account Flyout Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeMyOrderGuideCategory,
                displayName: "Include My Order Guide Category Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeMyOrderGuideSearch,
                displayName: "Include My Order Guide Search Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeSearchResult,
                displayName: "Include Search Result Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeViewItemListEvent,
                displayName: "Include View Item List Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeSelectItemListEvent,
                displayName: "Include Select Item List Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeAddToWishlistEvent,
                displayName: "Include Add To Wishlist Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeAddQuickOrderEvent,
                displayName: "Include Add Quick Order Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeBeginCheckoutEvent,
                displayName: "Include Begin Checkout Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeCheckoutShippingInfoEvent,
                displayName: "Include Checkout Shipping Info Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeCheckoutPaymentEvent,
                displayName: "Include Checkout Payment Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeViewPromotionEvent,
                displayName: "Include View Promotion Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeRegistrationEvent,
                displayName: "Include Registration Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includePDPViewEvent,
                displayName: "Include PDP View Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeCustomVirtualPageViewEvent,
                displayName: "Include Custom Virtual Page View Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includePunchOutCheckout,
                displayName: "Include Punchout Checkout Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeCancelPunchOutCheckout,
                displayName: "Include Cancel Punchout Checkout Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeNewAutoDeliveryEvent,
                displayName: "Include New Auto Delivery Event (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
        ],
    },
};

export default widgetModule;
